import { memo, useCallback, useContext } from "react";
import { getHideDiscountHBX } from "app/reducers/brandSelector";
import { getStore } from "app/configureStore";
import PropTypes from "prop-types";
import classNames from "classnames";
import AmountContainer from "app/pages/.shared/AmountContainer";
import Badge from "app/pages/.shared/Badge";
import { FormattedMessage } from "react-intl";
import { quotationBoardShape } from "app/utils/propTypes";
import Paragraphs from "app/pages/.shared/Paragraphs";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import "./QuotationBoard.scss";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS } from "app/constants";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import SavingsHighlight from "app/components/ui/SavingsHighlight/SavingsHighlight";
import { Button } from "app/components/ui/Button/Button";
import LineClampViewMore from "app/pages/.shared/LineClampViewMore/LineClampViewMore";
import { LessIcon, MoreIcon } from "app/icons/navigation";

const QuotationBoard = ({
	board = {},
	accommodationUpgradePrice = 0,
	isAccommodationIncluded,
	onChange,
}) => {
	const { brand } = useContext(AppGlobalsContext);
	const isBrandSV = brand === BRANDS.SV;
	const hideDiscountHBX = getHideDiscountHBX(getStore()?.getState());

	const onBoardSelect = useCallback(() => {
		if (typeof onChange === "function") {
			onChange(board);
		}
	}, [board]);

	const boardUpgradePrice = board?.upgradePrice || 0;

	const accommodationAndBoardUpgradPrice = boardUpgradePrice + accommodationUpgradePrice;
	const boardDiscount = Number(board?.discount) || 0;
	const showDiscount = boardDiscount > 0 && !hideDiscountHBX;
	const contentClassName = classNames("quotation-board__content", {
		"quotation-board__content--discounted": showDiscount,
	});
	const savingsHighlight = showDiscount ? (
		<div className="quotation-board__savings">
			<SavingsHighlight
				messageKey="general.save.amount"
				amountSaved={board.discount}
				trackEvent="booking_click_savings"
				trackData={{
					boardCode: board?.boardCode,
				}}
				description={<FormattedMessage id="savings.tooltip.label" />}
			/>
		</div>
	) : null;

	const isIncluded = Boolean(board.included && isAccommodationIncluded);

	return (
		<RadioFormik
			id={board.code}
			showIcon={true}
			name={"board"}
			toggle={onBoardSelect}
			value={board.code}
			className={classNames("quotation-board", {
				"quotation-board--discounted": showDiscount,
			})}
			data-testid={`quotation-board-${board.boardCode}`}
			body={
				board.paragraphs &&
				!isBrandSV &&
				board.paragraphs.length > 0 && (
					<div className="quotation-board__details">
						<LineClampViewMore
							enableShowMore={true}
							lineCount={3}
							text={
								<Paragraphs
									paragraphs={board.paragraphs}
									enableHTMLFormatting={true}
								/>
							}
							customShowMoreComponent={
								<div data-testid="read-more-button">
									<Button variant="borderless" startIcon={<MoreIcon />}>
										<FormattedMessage id="product.page.more.details" />
									</Button>
								</div>
							}
							customShowLessComponent={
								<div data-testid="read-more-button">
									<Button variant="borderless" startIcon={<LessIcon />}>
										<FormattedMessage id="product.page.less.details" />
									</Button>
								</div>
							}
						/>
					</div>
				)
			}
		>
			{isIncluded ? (
				<div className={contentClassName}>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						data-testid="board-label"
						className="quotation-board__label"
					>
						{board.label}
					</Typography>
					<div className="quotation-board__right">
						<Badge>
							<FormattedMessage id="general.included" />
						</Badge>
						{savingsHighlight}
					</div>
				</div>
			) : (
				<div className={contentClassName}>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						data-testid="board-label"
						className="quotation-board__label"
					>
						{board.label}
					</Typography>
					<div className="quotation-board__right">
						<div className="quotation-board__upgrade-price">
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE}>
								<AmountContainer
									amount={accommodationAndBoardUpgradPrice}
									prefix={<AmountSign sign="+" />}
								/>
							</Typography>
						</div>
						{savingsHighlight}
					</div>
				</div>
			)}
		</RadioFormik>
	);
};

QuotationBoard.propTypes = {
	board: quotationBoardShape,
	accommodationUpgradePrice: PropTypes.number,
	isAccommodationIncluded: PropTypes.bool,
	onChange: PropTypes.func,
};

export default memo(QuotationBoard);
